import { CalendarDate } from '@internationalized/date';

// ---------------------------------------------------------------------------
// Legacy Date Utilities
// ---------------------------------------------------------------------------
// IMPORTANT: This module contains legacy Date-based utilities.
// For new code, prefer zonedDateTimeUtils.ts which uses ZonedDateTime.
//
// These functions are kept for compatibility with existing components
// that still use JS Date objects (like legacy table components).
// ---------------------------------------------------------------------------

/**
 * Convert a JS Date (assumed to be midnight UTC) to CalendarDate.
 *
 * @param date - JS Date object (should be at midnight UTC)
 * @returns CalendarDate representing the same calendar day
 */
export const calendarDateFromDate = (date: Date): CalendarDate => {
  return new CalendarDate(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
  );
};

/**
 * Convert a CalendarDate to JS Date at midnight UTC.
 *
 * @param calendarDate - CalendarDate to convert
 * @returns JS Date at midnight UTC
 */
export const dateFromCalendarDate = (calendarDate: CalendarDate): Date => {
  return calendarDate.toDate('UTC');
};

// ---------------------------------------------------------------------------
// Time conversion utilities
// ---------------------------------------------------------------------------

// Time conversion utilities
export const convertTo12Hour = (
  hour24: number,
): { hour: number; period: 'AM' | 'PM' } => {
  if (hour24 === 0) return { hour: 12, period: 'AM' };
  if (hour24 < 12) return { hour: hour24, period: 'AM' };
  if (hour24 === 12) return { hour: 12, period: 'PM' };
  return { hour: hour24 - 12, period: 'PM' };
};

export const convertTo24Hour = (
  hour12: number,
  period: 'AM' | 'PM',
): number => {
  if (period === 'AM') {
    return hour12 === 12 ? 0 : hour12;
  } else {
    return hour12 === 12 ? 12 : hour12 + 12;
  }
};

/**
 * Get timezone abbreviation (e.g., "EST", "PST", "UTC")
 */
export const getTimezoneAbbreviation = (timezone: string): string => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    });

    const parts = formatter.formatToParts(now);
    const timeZonePart = parts.find((part) => part.type === 'timeZoneName');
    return timeZonePart?.value || timezone;
  } catch {
    return timezone;
  }
};
